import { useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

import { auth } from "../utils/firebase.utils";
import { track } from "../utils/track";
import { useAuth } from "../contexts/AuthContext";
import { requestSignIn } from "../access/authPrompt";
import { getFeature } from "../access/registry";

// A sign-in that takes longer than this has stopped being the same request.
export const RESUME_TTL_MS = 5 * 60 * 1000;

/**
 * Gate one action on sign-in, and finish it afterwards (FRS FR20).
 *
 *   const requireAuth = useRequireAuth("export-report");
 *   <Button onClick={() => requireAuth(() => exportReport(props))} />
 *
 * Signed in, the action runs at once. Signed out, the sign-in modal opens and,
 * once the user is in, the same action runs with no second click — through the
 * same code path a signed-in click takes, so the server checks it exactly as it
 * would any request (FR26). Cancel discards it.
 *
 * The resumed action is a specific intent, never a toggle: pass "save", not
 * "toggle saved", or a user whose account already had the address would have
 * it removed by signing in.
 *
 * Dropped rather than run if the user navigated away while signing in (the
 * router location key changed) or took longer than RESUME_TTL_MS (FR28): an
 * export of a page you have left is not what you asked for.
 *
 * Resolves with the action's result, or `{ status: "cancelled" }` /
 * `{ status: "dropped" }`.
 */
export const useRequireAuth = (featureId) => {
  const { status } = useAuth();
  const location = useLocation();
  const keyRef = useRef(location.key);
  keyRef.current = location.key;
  const statusRef = useRef(status);
  statusRef.current = status;

  return useCallback(
    async (action, { trigger = "click" } = {}) => {
      if (!getFeature(featureId)) {
        throw new Error(`useRequireAuth: unknown feature id "${featureId}"`);
      }
      if (statusRef.current === "signed-in") return action();

      const startedKey = keyRef.current;
      const startedAt = Date.now();

      // Never prompt while Firebase is still restoring a session: a signed-in
      // user who clicks during a reload just gets the action.
      await auth.authStateReady();
      if (auth.currentUser) return action();

      const signedIn = await requestSignIn({ featureId, trigger });
      if (!signedIn) return { status: "cancelled" };

      if (keyRef.current !== startedKey || Date.now() - startedAt > RESUME_TTL_MS) {
        track("gated_action_resumed", { feature_id: featureId, outcome: "dropped" });
        return { status: "dropped" };
      }
      try {
        // The fresh token comes from client.js's request interceptor.
        const result = await action();
        track("gated_action_resumed", { feature_id: featureId, outcome: "completed" });
        return result;
      } catch (e) {
        track("gated_action_resumed", { feature_id: featureId, outcome: "failed" });
        throw e;
      }
    },
    [featureId],
  );
};

export default useRequireAuth;
